import AngleRight from "@icons/angle-right.svg?react";
import AngleSmallDownSvg from "@icons/angle-small-down.svg?react";
import Cross from "@icons/cross.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  generatePath,
  Link,
  Outlet,
  resolvePath,
  useParams,
} from "react-router-dom";
import invariant from "tiny-invariant";

import { Button, primary } from "@/common/components/button/button";
import { Failed } from "@/common/components/info/info";
import { Metadata } from "@/common/components/metadata/metadata";
import * as OptionList from "@/common/components/option-list/option-list";
import { OrderItem } from "@/common/components/order-item/order-item";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { Params } from "@/common/components/quick-filters/quick-filters";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useCurrency } from "@/common/hooks/use-currency";
import { useLanguage } from "@/common/hooks/use-language";
import {
  currencyFormatter,
  numberFormatter,
} from "@/common/services/formatter";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { getAbsolutePath } from "../routes.utils";
import { selectPricesByQuarter } from "../shared/api/crop-prices.api";
import { offerQuery } from "../shared/api/get-offer";
import { storageQuery } from "../shared/api/get-storage";
import { CropParameters } from "../shared/components/crop-parameters/crop-parameters";
import { getUnit } from "../shared/utils/crop-unit";
import { path as transactionsAndOrdersPath } from "../transactions-and-orders/transactions-and-orders";
import { path as orderPath } from "../transactions-and-orders/transactions-and-orders.order";
import { PriceChartProvider } from "./components/price-chart/price-chart";
import { path as tradesPath } from "./trades";
import {
  cropLastPricesQuery,
  ordersQuery,
  sortLastPricesByQuarter,
} from "./trades.api";
import { path as sellPath } from "./trades.detail.sell";

type Fields = {
  marketPriceId: string;
};

const TradeDetail = () => {
  const { storageId } = usePageParams();
  const [, currency] = useCurrency();
  const { storage, offer, prices } = useData();
  const firstPrice = prices.at(0)?.marketPriceId;
  const { form, getCurrentPrice } = usePrice(firstPrice);
  const currentPrice = getCurrentPrice(prices);

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink
            omit={["date-from", "date-to"]}
            replace
            to={getAbsolutePath(tradesPath)}
          >
            <Cross />
          </SearchParamsLink>
        }
      >
        <>
          <Metadata title={offer.crop.name} />
          <FullscreenModalLayout.Title>
            {offer.crop.name}
          </FullscreenModalLayout.Title>
          <PropertiesList
            properties={[
              {
                label: <Trans>Farma</Trans>,
                value: storage.company.name,
              },
              {
                label: <Trans>Sklad</Trans>,
                value: storage.name,
              },
              {
                label: t`Rok sklizně`,
                value: offer.harvestYear,
              },
              {
                label: <Trans>Množství skladem</Trans>,
                value:
                  numberFormatter().format(offer.storage.offer.amount) +
                  " " +
                  getUnit(offer.crop.unit),
              },
              {
                label: <Trans>Celková hodnota</Trans>,
                value: prices.at(0)?.price
                  ? currencyFormatter(currency).format(
                      prices.at(0)!.price * offer.storage.offer.amount,
                    )
                  : "-",
              },
            ]}
          />
          <CropParameters
            cropId={offer.crop.id}
            traderId={currentPrice?.trader.id}
          />
          <Spacer className="h-6" />
          <Link
            className="text-can-forest-teal flex justify-center gap-1 self-center text-center"
            to={`../../storages/management/${storageId}`}
          >
            <Trans>Přejít do správy skladu</Trans>
            <AngleRight />
          </Link>
          <Spacer className="h-6" />
          {currentPrice ? (
            <>
              <h2 className="text-can-forest-teal font-bold">
                <Trans>Vývoj aktuální ceny</Trans>
              </h2>
              <Spacer className="h-4" />
              <Params>
                <PriceChartProvider
                  cropId={offer.crop.id}
                  quarterName={currentPrice.quarter.name}
                  storageId={offer.storage.id}
                  traderId={currentPrice.trader.id}
                />
              </Params>
              <Spacer className="h-6" />
              <form className="flex flex-col gap-4">
                <h2 className="text-can-forest-teal font-bold">
                  <Trans>Prodejní ceny</Trans>
                </h2>
                <FormProvider {...form}>
                  <PriceList
                    prices={prices.map((p) => ({
                      ...p,
                      id: p.marketPriceId,
                      price: currencyFormatter(currency).format(p.price),
                      quarter: p.quarter,
                    }))}
                  />
                </FormProvider>
              </form>
              <Spacer className="h-6" />
              <OrderList />
              <Spacer className="h-6" />
              <Button.Container>
                <SearchParamsLink className={primary} to={sellPath}>
                  <Trans>Prodat</Trans>
                </SearchParamsLink>
              </Button.Container>
            </>
          ) : null}
        </>
      </FullscreenModalLayout>
      <Outlet
        context={{
          company: storage.company,
          crop: offer.crop,
          marketPrice: {
            id: currentPrice?.marketPriceId,
            price: currentPrice?.price,
          },
          offer: {
            amount: offer.storage.offer.amount,
          },
          postalCode: storage.postalCode,
          quarter: currentPrice?.quarter,
          storage: { id: storage.id, name: storage.name },
          trader: currentPrice?.trader,
          traders: prices.map((p) => p.trader),
        }}
      />
    </>
  );
};

const OrderList = () => {
  const { offerId } = useParams();
  invariant(offerId);
  const orders = useQuery(ordersQuery({ offerId }));

  let Jsx = (
    <div className="mx-auto">
      <Spinner />
    </div>
  );

  if (orders.status === "error") {
    Jsx = <Failed error={orders.error} />;
  }

  if (orders.status === "success") {
    Jsx = (
      <div className="flex flex-col gap-y-6">
        <ul className="isolate flex flex-col gap-y-2">
          {orders.data.map((order) => (
            <OrderItem key={order.id} {...order}>
              <Link
                to={resolvePath(
                  generatePath(orderPath, {
                    offerId,
                    orderId: order.id,
                  }),
                  getAbsolutePath(transactionsAndOrdersPath),
                )}
              ></Link>
            </OrderItem>
          ))}
        </ul>
        <Link
          className="text-can-forest-teal flex items-center justify-center"
          to={getAbsolutePath(transactionsAndOrdersPath)}
        >
          <Trans>Ukázat všechny příkazy</Trans>
          <AngleSmallDownSvg className="[&_path]:fill-can-forest-teal -rotate-90" />
        </Link>
      </div>
    );
  }

  if (orders.status === "success" && orders.data.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-4">
      <h2 className="text-can-forest-teal font-bold">
        <Trans>Aktivní příkazy</Trans>
      </h2>
      {Jsx}
    </div>
  );
};

type PriceListProps = {
  prices: {
    id: string;
    quarter: {
      id: string;
      name: string;
    };
    price: string;
    trader: {
      logo: string;
    };
  }[];
};

const PriceList = ({ prices }: PriceListProps) => {
  return (
    <OptionList.List>
      {prices.map((price) => (
        <OptionList.Item key={price.id}>
          <OptionList.Label>
            <OptionList.Checkbox
              name="marketPriceId"
              options={{
                required: t`Vyberte prosím kvartál`,
              }}
              value={price.id}
            >
              <span>{price.quarter.name}</span>
            </OptionList.Checkbox>
            <span className="flex items-center">
              <img src={price.trader.logo} style={{ height: "20px" }} />
            </span>

            <b>{price.price}</b>
          </OptionList.Label>
        </OptionList.Item>
      ))}
    </OptionList.List>
  );
};

const usePageParams = () => {
  const { storageId, offerId, quarterId } = useParams();
  invariant(storageId);
  invariant(offerId);
  invariant(quarterId);
  return { offerId, quarterId, storageId };
};

const useData = () => {
  const [, currency] = useCurrency();
  const locale = useLanguage();
  const { offerId, quarterId, storageId } = usePageParams();

  const storage = useSuspenseQuery(storageQuery({ id: storageId, locale }));
  const offer = useSuspenseQuery(
    offerQuery({
      id: offerId,
      locale,
    }),
  );
  const prices = useSuspenseQuery({
    ...cropLastPricesQuery({
      cropId: offer.data.crop.id,
      // @ts-expect-error discrepancy between CF API and IDSign API
      currency,
      storageId,
    }),
    select: (data) => {
      return orderBy(
        selectPricesByQuarter(sortLastPricesByQuarter(data), quarterId),
        (item) => item.price,
        "desc",
      );
    },
  });

  return { offer: offer.data, prices: prices.data, storage: storage.data };
};

const usePrice = (initialPrice?: string) => {
  const form = useForm<Fields>({
    defaultValues: {
      marketPriceId: initialPrice,
    },
  });
  const currentPriceId = form.watch("marketPriceId");

  React.useEffect(() => {
    form.reset({
      marketPriceId: initialPrice,
    });
  }, [form, initialPrice]);

  const getCurrentPrice = React.useCallback(
    <T extends { marketPriceId: string }>(prices: Array<T>) =>
      prices.find((p) => p.marketPriceId === currentPriceId),
    [currentPriceId],
  );

  return {
    form,
    getCurrentPrice,
  };
};

const path = ":storageId/offers/:offerId/quarter/:quarterId";

export { path, TradeDetail };
